import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';

import * as actionCreators from '../actions/mailingCreate';
import { MailingCreate } from '../components/MailingCreate';
import { buildSortByParam } from '../helpers/paramBuilder';
import connectOptions, { mergeProps } from '../utils/connectOptions';
import { showError } from '../utils/MessagePopup';
import { isResearcher } from '../utils/checkPermissions';
import config from '../config';
import { usePopupContext } from '../contexts/PopupContext';

const { func, object } = PropTypes;

const getQuery = location => {
  const searchParams = new URLSearchParams(location.search);
  const recordOwnerId = searchParams.get('recordOwnerId');
  const recordSubOwnerId = searchParams.get('recordSubOwnerId');
  const buyerId = searchParams.get('buyerId');
  const projectId = searchParams.get('projectId');
  const activity = searchParams.get('activity');
  const isLetter = searchParams.get('isLetter');
  const isEmail = searchParams.get('isEmail');
  const beforeDate = searchParams.get('beforeDate');

  return {
    recordOwnerId,
    recordSubOwnerId,
    buyerId,
    projectId,
    activity,
    beforeDate,
    isLetter: isLetter === 'true' ? 1 : 0,
    isEmail: isEmail === 'true' ? 1 : 0,
  };
};

const hasAccess = user => {
  if (!user) return false;

  return !isResearcher(user.getIn(['roles', 0, 'slug'], null));
};

/** Mailing create page container. */
const MailingCreateContainer = props => {
  const {
    location,
    history,
    user,
    mailingCreate,
    getPMHeader,
    getPMTargets,
    updateMailingCreate,
    initCreateMailing,
    clearCreateMailingError,
    createMailing,
    updateSelectedRow,
    updateSelectedRowsByQuantity,
    updateAllRows,
  } = props;

  const { openPopup, closePopup } = usePopupContext();
  const [selectionQuantity, setSelectionQuantity] = useState(0);
  const userHasAccess = hasAccess(user);

  const hasMountedRef = useRef(false);
  const previousMailingCreateRef = useRef(mailingCreate);

  useEffect(() => {
    if (!userHasAccess) {
      showError(openPopup, config.permisionError, () => {
        history.push('/');
        closePopup();
      });

      return;
    }

    const query = getQuery(location);

    initCreateMailing();
    getPMHeader(query);
    getPMTargets(query);

    updateMailingCreate({
      filterName: 'header',
      filterData: query,
    });
  }, []);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      previousMailingCreateRef.current = mailingCreate;

      return;
    }

    const previousMailingCreate = previousMailingCreateRef.current;

    previousMailingCreateRef.current = mailingCreate;

    if (previousMailingCreate !== mailingCreate) {
      const oldSortBy = buildSortByParam(previousMailingCreate);
      const sortBy = buildSortByParam(mailingCreate);

      if (sortBy !== oldSortBy) {
        const query = getQuery(location);

        query.page = 1;
        query.sortBy = sortBy;

        getPMTargets(query);
      }
    }

    const createMailingError = mailingCreate.get('createMailingError');

    if (createMailingError) {
      clearCreateMailingError();
      showError(openPopup, ['Error creating mailing'], null);
    }
  }, [mailingCreate, location, getPMTargets, clearCreateMailingError, openPopup]);

  const handleGetNextPageData = useCallback(
    page => {
      const sortBy = buildSortByParam(mailingCreate);
      const query = getQuery(location);

      query.page = page;
      query.sortBy = sortBy;

      getPMTargets(query);
    },
    [mailingCreate, location, getPMTargets],
  );

  const handleChange = useCallback(
    data => {
      updateMailingCreate({
        filterName: data.filterName,
        filterData: data.filterData,
      });
    },
    [updateMailingCreate],
  );

  const handleDoubleClickRow = useCallback(row => {
    window.open(`/company/${row.data.targetId}/target`, '_blank');
  }, []);

  const handleReload = useCallback(() => {
    const query = getQuery(location);

    query.page = 1;
    initCreateMailing();
    getPMTargets(query);
  }, [location, initCreateMailing, getPMTargets]);

  const handleCreateMailing = useCallback(() => {
    const isCreatingMailing = mailingCreate.get('isCreatingMailing');
    const isUnableToCreateMailing = mailingCreate.get('isUnableToCreateMailing');

    if (!isCreatingMailing && !isUnableToCreateMailing) {
      let sourceList = '';
      let confirmMessage = '';

      if (mailingCreate.get('selectList').size > 0) {
        sourceList = mailingCreate.get('selectList');
        confirmMessage = `You are about to create a mailing from ${sourceList.size} selected targets. Proceed?`;
      } else {
        sourceList = mailingCreate.get('queryResults').map(queryResult => queryResult.get('id'));
        confirmMessage = `You are about to create a mailing from all targets (${sourceList.size} total). Proceed?`;
      }

      const selectedIds = sourceList.reduce((accumulator, id) => {
        accumulator.push(id);

        return accumulator;
      }, []);

      openPopup('ConfirmPopup', {
        message: confirmMessage,
        onOk: () => {
          createMailing({
            selected_ids: selectedIds,
            user_id: user.get('id'),
          });
          closePopup();
        },
        onCancel: () => closePopup(),
        yes: 'Yes',
        no: 'No',
      });
    }
  }, [mailingCreate, user, createMailing, openPopup, closePopup]);

  const handleChangeSelectionQuantity = useCallback(
    event => {
      const queryResultSize = mailingCreate.get('queryResults').size;
      const nextSelectionQuantity = event.target.value;

      if (Number(nextSelectionQuantity) > queryResultSize) return;

      setSelectionQuantity(nextSelectionQuantity);
    },
    [mailingCreate],
  );

  const handleSelectinoByQuantity = useCallback(() => {
    updateSelectedRowsByQuantity({
      field: 'selected',
      value: true,
      quantity: selectionQuantity,
    });
  }, [updateSelectedRowsByQuantity, selectionQuantity]);

  const handleSelection = useCallback(
    value => {
      updateAllRows({
        field: 'selected',
        value,
      });
    },
    [updateAllRows],
  );

  if (!userHasAccess) return null;

  return (
    <div className="full-height">
      <Helmet title="Create New Mailing Page" />
      <MailingCreate
        mailingCreate={mailingCreate}
        onChange={handleChange}
        onChangeSelectionQuantity={handleChangeSelectionQuantity}
        onClickSelectionControl={handleSelection}
        onCreateMailing={handleCreateMailing}
        onDoubleClickRow={handleDoubleClickRow}
        onGetNextPageData={handleGetNextPageData}
        onReload={handleReload}
        onSelectByQuantity={handleSelectinoByQuantity}
        onUpdateSelectedRow={updateSelectedRow}
        selectionQuantity={selectionQuantity}
      />
    </div>
  );
};

MailingCreateContainer.propTypes = {
  getPMHeader: func.isRequired,
  getPMTargets: func.isRequired,
  mailingCreate: object.isRequired,
  updateMailingCreate: func.isRequired,
};

const mapStateToProps = state => ({
  mailingCreate: state.mailingCreate,
  user: state.auth.get('user', null),
});

const mapDispatchToProps = dispatch => ({
  ...bindActionCreators(actionCreators, dispatch),
});

export { MailingCreateContainer };

export default connect(mapStateToProps, mapDispatchToProps, mergeProps, connectOptions)(memo(MailingCreateContainer));
